import React, { useState } from 'react'
import { Tooltip, PlacesType } from 'react-tooltip'
import styled from 'styled-components'
import dwaLightWeb from '@nexiahome/ui-common/themes/dwa-light-web'
import useResponsiveScreen from '@nexiahome/ui-common/themes/util/useResponsiveScreen'

import Modal from 'components/Modal'

import Alerts from 'pages/shared/AlertsTooltip/Alerts'
import Spinner from 'components/Spinner'
import TooltipContent from 'pages/shared/AlertsTooltip/TooltipContent'

import { color } from 'styles/colors'
import { Alert, DeviceConnectionState } from 'types/Alert'

const ARROW_SIZE = 18
const ARROW_OFFSET = ARROW_SIZE / 2

type Props = {
  id?: string,
  alerts: Array<Alert>,
  deviceConnectionState?: Array<DeviceConnectionState> | null
  isLoaded?: boolean
  hideArrow?: boolean
  place?: PlacesType | undefined
}

const AlertsTooltip = ({
  id = 'alert-tooltip',
  alerts,
  deviceConnectionState = [],
  isLoaded = true,
  hideArrow = false,
  place
}: Props) => {
  const { breakpoints } = dwaLightWeb
  const { isPortrait, screenSize } = useResponsiveScreen({ breakpoints })
  const [isTooltipOpen, setTooltipOpen] = useState(false)
  const [displayModal, setDisplayModal] = useState(false)
  const [displayAlertIds, setDisplayAlertIds] = useState<string | null>(null)

  const OnModalCloseCallback = () => {
    setTooltipOpen(false)
    setDisplayModal(false)
  }

  return (
    <>
      <S.Tooltip
          id={id}
          imperativeModeOnly={displayModal}
          clickable
          place={place}
          offset={15}
          noArrow={hideArrow}
          delayHide={200}
          isOpen={isTooltipOpen}
          setIsOpen={(open) => setTooltipOpen(open)}
          afterShow={() => {
            setDisplayModal((screenSize === 'sm' || screenSize === 'xs') && !isPortrait)
          }}
          render={({ content }) => {
            if (!content && isLoaded) return null
            if (!isLoaded) {
              return (
                <S.SpinnerContainer>
                  <Spinner variant="cell" />
                </S.SpinnerContainer>
              )
            } else {
              return (
                <TooltipContent
                  allAlerts={alerts}
                  deviceConnectionState={deviceConnectionState}
                  screenSize={screenSize}
                  displayAlertIds={content}
                  isPortrait={isPortrait}
                  setDisplayAlertIds={setDisplayAlertIds}
                />
              )
            }
          }}
      />
      {displayModal && (
        <S.Modal isDisplayed={displayModal} fullScreen scroll="paper" closeCallback={OnModalCloseCallback}>
          <Alerts alerts={alerts} displayAlertIds={displayAlertIds} deviceConnectionState={deviceConnectionState} />
        </S.Modal>)
      }
    </>
  )
}

const S = {
  // using important to override the default tooltip style
  Tooltip: styled(Tooltip)`
    background-color: ${color('paneBackground')} !important;
    z-index: 9999;
    border: 1px solid ${color('tooltipBorder')} !important;
    border-radius: 16px !important;
    opacity: 1 !important;
    padding: 0 !important;
    box-shadow: 0px 8px 16px 0px ${color('tooltipShadow')} !important;

    .react-tooltip-content-wrapper {
      padding: 0 !important;
    }

    .react-tooltip-arrow {
      width: ${ARROW_SIZE}px;
      height: ${ARROW_SIZE}px;
      box-sizing: border-box;
      background: ${color('paneBackground')};
      border: 1px solid ${color('tooltipBorder')};
      border-bottom-right-radius: 6px;
    }

    &[class*='react-tooltip__place-top'] .react-tooltip-arrow {
      bottom: -${ARROW_OFFSET}px !important;
    }

    &[class*='react-tooltip__place-bottom'] .react-tooltip-arrow {
      top: -${ARROW_OFFSET}px !important;
    }

    &[class*='react-tooltip__place-left'] .react-tooltip-arrow {
      right: -${ARROW_OFFSET}px !important;
    }

    &[class*='react-tooltip__place-right'] .react-tooltip-arrow {
      left: -${ARROW_OFFSET}px !important;
    }
  `,
  Modal: styled(Modal)`
    overflow: hidden;
  `,
  SpinnerContainer: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
    width: 200px;
  `
}

export default AlertsTooltip
